import { api } from "@/lib/apiClient";
import type { DokumentTyp } from "@/lib/dokumente";
import type {
  GdDocumentTemplateDetail,
  GdDocumentTemplateListItem,
  GdTemplatePosition,
} from "@/pages/dokumente/documentTemplateModel";

export interface CreateGdVorlagePayload {
  name: string;
  beschreibung?: string | null;
  dokument_typ: DokumentTyp;
  betreff?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  positionen: GdTemplatePosition[];
  sort_index?: number;
  schlagworte?: string[];
  ordner_ids?: number[];
  sordner_ids?: number[];
  gueltig_ab?: string | null;
}

export interface UpdateGdVorlagePayload {
  name?: string;
  beschreibung?: string | null;
  betreff?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  positionen?: GdTemplatePosition[];
  sort_index?: number;
  schlagworte?: string[];
  ordner_ids?: number[];
  sordner_ids?: number[];
  gueltig_ab?: string | null;
}

export interface CreateGdVorlageVersionPayload {
  gueltig_ab: string;
  name?: string | null;
}

export interface ListGdVorlagenOptions {
  typ?: DokumentTyp;
  q?: string;
  auftragId?: number | null;
  sordnerId?: number | null;
  ordnerId?: number | null;
  stichtag?: string | null;
  alleVersionen?: boolean;
}

export function listGdVorlagen(
  typOrOptions?: DokumentTyp | ListGdVorlagenOptions,
): Promise<GdDocumentTemplateListItem[]> {
  const options: ListGdVorlagenOptions =
    typeof typOrOptions === "string" || typOrOptions == null
      ? { typ: typOrOptions }
      : typOrOptions;
  const params = new URLSearchParams();
  if (options.typ) params.set("typ", options.typ);
  if (options.q?.trim()) params.set("q", options.q.trim());
  if (options.auftragId != null && options.auftragId > 0) {
    params.set("auftrag_id", String(options.auftragId));
  }
  if (options.sordnerId != null && options.sordnerId > 0) {
    params.set("sordner_id", String(options.sordnerId));
  }
  if (options.ordnerId != null && options.ordnerId > 0) {
    params.set("ordner_id", String(options.ordnerId));
  }
  if (options.stichtag?.trim()) params.set("stichtag", options.stichtag.trim());
  if (options.alleVersionen) params.set("alle_versionen", "true");
  const query = params.toString();
  return api.get<GdDocumentTemplateListItem[]>(`/dokumente/vorlagen${query ? `?${query}` : ""}`);
}

export function getGdVorlage(id: number): Promise<GdDocumentTemplateDetail> {
  return api.get<GdDocumentTemplateDetail>(`/dokumente/vorlagen/${id}`);
}

export function createGdVorlage(payload: CreateGdVorlagePayload): Promise<GdDocumentTemplateDetail> {
  return api.post<GdDocumentTemplateDetail>("/dokumente/vorlagen", payload, {
    // Große Vorlagen (viele Positionen) – gleicher Lastpfad wie Entwurf-Save.
    timeoutMs: 180_000,
  });
}

export function createGdVorlageVersion(
  id: number,
  payload: CreateGdVorlageVersionPayload,
): Promise<GdDocumentTemplateDetail> {
  return api.post<GdDocumentTemplateDetail>(`/dokumente/vorlagen/${id}/version`, payload);
}

export function updateGdVorlage(
  id: number,
  payload: UpdateGdVorlagePayload,
): Promise<GdDocumentTemplateDetail> {
  return api.patch<GdDocumentTemplateDetail>(`/dokumente/vorlagen/${id}`, payload);
}

export function reorderGdVorlagen(
  items: Array<{ id: number; sort_index: number }>,
): Promise<{ ok: boolean; updated: number }> {
  return api.put<{ ok: boolean; updated: number }>("/dokumente/vorlagen/sort", { items });
}

export function deleteGdVorlage(id: number): Promise<{ ok: boolean; id: number }> {
  return api.del(`/dokumente/vorlagen/${id}`);
}

export function formatVorlageGueltigAb(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
